import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../component/common/Loader';
import { formatBigNumber } from '../SleepHub/sleepHubApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_GRID_NO_X
} from '../../component/common/designSystem';
import {
  PROGRAMME_COUNT_RAMP,
  programmeColor,
  expiryChipClass,
  expiryLabel,
  schemeStatusChipClass
} from './perksTheme';

/**
 * Perks → Overview.
 *
 * The one page that spans all four programmes, so it answers the questions no
 * single programme dashboard can:
 *  1. Headline — how far perks reach into the workforce at all.
 *  2. Coverage — how many programmes each person is on, and how many are on
 *     nothing.
 *  3. Per programme — what is configured against who actually took it up.
 *
 * The gap between "configured" and "people" is the point of this page: a
 * company with six schemes and four members has a communication problem, not
 * a benefits problem, and no single programme tab makes that visible.
 */
const OverviewDashboard = ({ fetcher, populationLabel = 'employees', onOpenProgramme }) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const programmes = stats?.programmes || [];
  const programmeCountSplit = stats?.programmeCountSplit || [];

  // Only the programmes this company actually has — a card for a programme
  // that is switched off would invite a click that 403s.
  const availability = stats?.availability;
  const availableProgrammes = availability
    ? programmes.filter((programme) => {
      const key = {
        instant_support: 'instantSupport',
        salary_sacrifice: 'salarySacrifice',
        health_scheme: 'healthScheme',
        custom_perks: 'customPerks'
      }[programme.key];
      return availability[key];
    })
    : programmes;

  const uptakeOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: availableProgrammes.map((programme) => programmeColor(programme.key)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: true }
    },
    dataLabels: { enabled: true, style: { fontSize: '12px' } },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: { categories: availableProgrammes.map((programme) => programme.name) },
    tooltip: {
      y: { formatter: (val) => `${val.toLocaleString()} ${populationLabel}` }
    }
  };

  const anyUptake = availableProgrammes.some((programme) => programme.people > 0);

  // People on nothing — the actionable half of the coverage chart.
  const onNothing = Math.max((stats?.totalUsers || 0) - (stats?.reached || 0), 0);

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Perks Overview | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title={`Total ${populationLabel === 'members' ? 'Members' : 'Employees'}`}
          value={stats ? formatBigNumber(stats.totalUsers) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title="On at Least One Perk"
          value={stats ? formatBigNumber(stats.reached) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Perks Reach"
          value={stats ? `${stats.reach}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.personalBest}
          title="On Two or More"
          value={stats ? formatBigNumber(stats.multiProgramme) : '-'}
        />
      </div>

      {/* ---------- Coverage ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <BigStatPanel
          title="Perks Reach"
          subtitle={`Share of ${populationLabel} on any programme`}
          value={stats ? `${stats.reach}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.reached)} of ${formatBigNumber(
                stats.totalUsers
              )} ${populationLabel}`
              : null
          }
        />

        <MiniDonutCard
          title="Programmes per Person"
          subtitle="How many perks each person is on"
          data={programmeCountSplit}
          colors={PROGRAMME_COUNT_RAMP}
          unit={populationLabel}
          legendLimit={5}
        />

        {/*
          Coverage as two numbers rather than five: the split above shows the
          shape, this shows the one decision — who is not being reached.
        */}
        <Panel title="Coverage" subtitle="Who your perks are reaching">
          <ProgressList
            items={[
              { label: 'On at least one perk', count: stats?.reached || 0 },
              { label: 'On nothing', count: onNothing }
            ]}
            unit={populationLabel}
          />
        </Panel>
      </div>

      {/* ---------- Uptake by programme ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Uptake by Programme" subtitle={`${populationLabel} on each programme`}>
          {anyUptake ? (
            <Chart
              type="bar"
              height={320}
              width="100%"
              series={[
                {
                  name: 'People',
                  data: availableProgrammes.map((programme) => programme.people)
                }
              ]}
              options={uptakeOptions}
            />
          ) : (
            <PanelEmpty>Nobody is enrolled on a perk yet.</PanelEmpty>
          )}
        </Panel>

        <Panel
          title="Programme Breakdown"
          subtitle="What is set up against who is on it"
        >
          {availableProgrammes.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Programme</th>
                  <th className="py-2 pr-4 font-medium">Set up</th>
                  <th className="py-2 pr-4 font-medium text-right">People</th>
                  <th className="py-2 font-medium text-right">Reach</th>
                </>
              }
            >
              {availableProgrammes.map((programme) => (
                <tr
                  key={programme.key}
                  className={`${PANEL_ROW_CLASS}${onOpenProgramme ? ' cursor-pointer' : ''}`}
                  onClick={onOpenProgramme ? () => onOpenProgramme(programme.key) : undefined}
                >
                  <td className="py-2 pr-4">
                    <span className="inline-flex items-center gap-2">
                      {/* Swatch ties the row to its bar in the chart. */}
                      <span
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ backgroundColor: programmeColor(programme.key) }}
                      />
                      <span className="truncate">{programme.name}</span>
                    </span>
                  </td>
                  <td className="py-2 pr-4">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${schemeStatusChipClass(
                          programme.configuredLabel
                        )}`}
                      >
                        {programme.configuredLabel}
                      </span>
                      {/* Only the EAP carries an expiry at the programme level. */}
                      {programme.daysToExpiry != null && (
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${expiryChipClass(
                            programme.daysToExpiry
                          )}`}
                        >
                          {expiryLabel(programme.daysToExpiry)}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-right font-semibold">
                    {formatBigNumber(programme.people)}
                  </td>
                  <td className="py-2 text-right">{programme.reach}%</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No perks programmes are enabled for your company.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

OverviewDashboard.propTypes = {
  fetcher: PropTypes.func.isRequired,
  populationLabel: PropTypes.string,
  /** Called with a programme key when a breakdown row is clicked. */
  onOpenProgramme: PropTypes.func
};

export default OverviewDashboard;
